import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, CheckCircle2, Undo2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useConstruction } from '../../context/ConstructionContext';
import { toDate, toIsoDate } from '../../construction/evm';
import { formatPct } from '../../construction/money';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ConstructionShell, { FormError, Panel, inputClass, labelClass, runAction } from './ConstructionShell';
import { t } from '../../i18n';

export const itemStatus = (row) => {
  if (row.status === 'detenida') return { label: 'Detenida', tone: 'neutral' };
  if (row.actual >= 100) return { label: 'Completada', tone: 'success' };
  if (row.planned > 0 && row.actual + 10 < row.planned) return { label: 'Atrasada', tone: 'danger' };
  if (row.actual > 0) return { label: 'En curso', tone: 'info' };
  return { label: 'No iniciada', tone: 'neutral' };
};

const MILESTONE_TONE = { cumplido: 'success', vencido: 'danger', proximo: 'warning', pendiente: 'neutral' };
const MILESTONE_LABEL = { cumplido: 'Cumplido', vencido: 'Vencido', proximo: 'Próximo', pendiente: 'Pendiente' };

const emptyForm = { id: null, parentId: '', code: '', name: '', weight: '', bac: '', baselineStart: '', baselineEnd: '', actualStart: '', actualEnd: '', predecessors: [], responsible: '', packageId: '', status: 'auto' };

const Gantt = ({ rows }) => {
  const dated = rows.filter((row) => row.baselineStart && row.baselineEnd);
  if (dated.length === 0) return <p className="text-xs text-neutral-500">{t('Ingresa fechas de línea base para ver el Gantt.')}</p>;
  const min = Math.min(...dated.map((row) => toDate(row.baselineStart).getTime()));
  const max = Math.max(...dated.map((row) => toDate(row.baselineEnd).getTime()), Date.now());
  const span = Math.max(1, max - min);
  const pos = (date) => ((toDate(date).getTime() - min) / span) * 100;
  const todayPos = ((Date.now() - min) / span) * 100;
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        <div className="flex text-[11px] text-neutral-500 mb-1">
          <span className="w-56 shrink-0" />
          <div className="relative flex-1 h-4">
            <span className="absolute left-0">{toIsoDate(new Date(min))}</span>
            <span className="absolute right-0">{toIsoDate(new Date(max))}</span>
          </div>
        </div>
        {rows.map((row) => (
          <div key={row.id} className="flex items-center h-7 border-t border-neutral-100">
            <span className="w-56 shrink-0 truncate text-xs text-neutral-700" style={{ paddingLeft: row.depth * 12 }} title={row.name}>
              <span className="text-neutral-400 mr-1">{row.code}</span>
              {row.name}
            </span>
            <div className="relative flex-1 h-full">
              <div className="absolute top-0 bottom-0 border-l border-danger-500 border-dashed" style={{ left: `${todayPos}%` }} title={t('Hoy')} />
              {row.baselineStart && row.baselineEnd && (
                <div
                  className={`absolute top-1.5 h-4 rounded-sm ${row.isLeaf ? 'bg-neutral-200' : 'bg-neutral-300'}`}
                  style={{ left: `${pos(row.baselineStart)}%`, width: `${Math.max(0.5, pos(row.baselineEnd) - pos(row.baselineStart))}%` }}
                  title={`${row.baselineStart} → ${row.baselineEnd}`}
                >
                  <div className="h-full rounded-sm bg-brand-500" style={{ width: `${row.actual}%` }} />
                </div>
              )}
            </div>
          </div>
        ))}
        <div className="flex gap-4 mt-2 text-[11px] text-neutral-500">
          <span className="flex items-center gap-1"><span className="w-3 h-2 bg-neutral-200 rounded-sm" />{t('Línea base')}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 bg-brand-500 rounded-sm" />{t('Avance real')}</span>
          <span className="flex items-center gap-1"><span className="w-3 border-t border-dashed border-danger-500" />{t('Hoy')}</span>
        </div>
      </div>
    </div>
  );
};

const ScheduleContent = ({ summary }) => {
  const { parks } = useAppContext();
  const { can, upsertWbsItem, deleteWbsItem, upsertMilestone, completeMilestone } = useConstruction();
  const park = parks.find((candidate) => candidate.id === summary.parkId);
  const canEdit = can('schedule.edit');
  const [view, setView] = useState('table');
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [milestoneForm, setMilestoneForm] = useState({ name: '', targetDate: '', wbsItemId: '' });
  const [milestoneError, setMilestoneError] = useState('');

  const rows = summary.evm.rows;
  const codeById = useMemo(() => Object.fromEntries(summary.items.map((item) => [item.id, item.code])), [summary.items]);

  const tableRows = rows.map((row) => ({
    ...row,
    statusLabel: itemStatus(row).label,
    predecessorCodes: (row.predecessors || []).map((id) => codeById[id]).filter(Boolean).join(', ')
  }));

  const columns = [
    {
      key: 'name',
      label: 'Partida',
      render: (value, row) => (
        <span style={{ paddingLeft: row.depth * 14 }} className={row.isLeaf ? '' : 'font-semibold'}>
          <span className="text-neutral-400 mr-1.5">{row.code}</span>
          {value}
        </span>
      )
    },
    { key: 'weightPct', label: 'Ponderación', align: 'right', sortable: true, render: (value) => formatPct(value) },
    { key: 'baselineStart', label: 'Inicio LB', sortable: true },
    { key: 'baselineEnd', label: 'Fin LB', sortable: true },
    { key: 'actualStart', label: 'Inicio real', render: (value) => value || '—' },
    { key: 'actualEnd', label: 'Fin real', render: (value) => value || '—' },
    { key: 'predecessorCodes', label: 'Predecesoras', render: (value) => value || '—' },
    { key: 'responsible', label: 'Responsable', render: (value) => value || '—' },
    { key: 'actual', label: 'Avance', align: 'right', sortable: true, render: (value) => formatPct(value) },
    {
      key: 'statusLabel',
      label: 'Estado',
      render: (value, row) => <Badge tone={itemStatus(row).tone}>{t(value)}</Badge>
    }
  ];
  if (canEdit) {
    columns.push({
      key: 'id',
      label: 'Acciones',
      render: (value, row) => (
        <div className="flex gap-1">
          <button type="button" className="p-1 text-neutral-500 hover:text-neutral-900" title={t('Editar')} onClick={() => { setError(''); setForm({ ...emptyForm, ...row, parentId: row.parentId || '', weight: row.weight ?? '', bac: row.bac ?? '' }); }}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className="p-1 text-neutral-500 hover:text-danger-600"
            title={t('Eliminar')}
            onClick={() => {
              if (window.confirm(t('¿Eliminar la partida, sus subpartidas y sus avances?'))) runAction(() => deleteWbsItem(value), setError);
            }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )
    });
  }

  const submit = (event) => {
    event.preventDefault();
    const saved = runAction(() => upsertWbsItem({ ...form, parkId: summary.parkId, parentId: form.parentId || null, packageId: form.packageId || null }), setError);
    if (saved) setForm(null);
  };

  const field = (key) => ({ value: form?.[key] ?? '', onChange: (event) => setForm((prev) => ({ ...prev, [key]: event.target.value })) });

  const leafCount = rows.filter((row) => row.isLeaf).length;
  const weightTotal = rows.filter((row) => row.isLeaf).reduce((sum, row) => sum + (Number(row.weight) || 0), 0);

  return (
    <div className="space-y-4">
      <Panel
        title="EDT / WBS del proyecto"
        description="Fase → partida → subpartida. La ponderación y el BAC se definen en las partidas hoja."
        actions={
          <>
            <div className="inline-flex rounded border border-neutral-300 overflow-hidden">
              {['table', 'gantt'].map((mode) => (
                <button key={mode} type="button" onClick={() => setView(mode)} className={`h-7 px-2.5 text-xs font-medium ${view === mode ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-700'}`}>
                  {t(mode === 'table' ? 'Tabla' : 'Gantt')}
                </button>
              ))}
            </div>
            {canEdit && (
              <Button variant="primary" size="sm" icon={Plus} onClick={() => { setError(''); setForm({ ...emptyForm }); }}>
                {t('Nueva partida')}
              </Button>
            )}
          </>
        }
      >
        {leafCount > 0 && weightTotal > 0 && Math.round(weightTotal) !== 100 && (
          <p className="mb-3 text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded px-2.5 py-1.5">
            {t('Las ponderaciones de las partidas hoja suman {0}%. Se normalizan a 100% para el cálculo.', Math.round(weightTotal))}
          </p>
        )}
        {form && (
          <form onSubmit={submit} className="mb-4 border border-neutral-200 rounded p-3 bg-neutral-50 space-y-3">
            <p className="text-sm font-semibold text-neutral-900">{t(form.id ? 'Editar partida' : 'Nueva partida')}</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>{t('Depende de (fase / partida padre)')}</label>
                <select className={inputClass} {...field('parentId')}>
                  <option value="">{t('— Nivel superior (fase) —')}</option>
                  {rows.filter((row) => row.id !== form.id && row.depth < 2).map((row) => (
                    <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('Código')}</label>
                <input className={inputClass} placeholder="2.1" {...field('code')} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Nombre')}</label>
                <input className={inputClass} {...field('name')} required />
              </div>
              <div>
                <label className={labelClass}>{t('Ponderación (%)')}</label>
                <input type="number" min="0" step="0.1" className={inputClass} {...field('weight')} />
              </div>
              <div>
                <label className={labelClass}>{t('Presupuesto BAC ({0})', summary.currency)}</label>
                <input type="number" min="0" step="1" className={inputClass} {...field('bac')} />
              </div>
              <div>
                <label className={labelClass}>{t('Inicio línea base')}</label>
                <input type="date" className={inputClass} {...field('baselineStart')} />
              </div>
              <div>
                <label className={labelClass}>{t('Fin línea base')}</label>
                <input type="date" className={inputClass} {...field('baselineEnd')} />
              </div>
              <div>
                <label className={labelClass}>{t('Inicio real')}</label>
                <input type="date" className={inputClass} {...field('actualStart')} />
              </div>
              <div>
                <label className={labelClass}>{t('Fin real')}</label>
                <input type="date" className={inputClass} {...field('actualEnd')} />
              </div>
              <div>
                <label className={labelClass}>{t('Responsable')}</label>
                <input className={inputClass} {...field('responsible')} />
              </div>
              <div>
                <label className={labelClass}>{t('Paquete WBS (imputación de OC)')}</label>
                <select className={inputClass} {...field('packageId')}>
                  <option value="">{t('Sin paquete')}</option>
                  {(park?.packages || []).map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>{`${pkg.code} ${pkg.name}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('Estado')}</label>
                <select className={inputClass} {...field('status')}>
                  <option value="auto">{t('Automático (según avance)')}</option>
                  <option value="detenida">{t('Detenida')}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Predecesoras')}</label>
                <select
                  multiple
                  className={`${inputClass} h-20`}
                  value={form.predecessors || []}
                  onChange={(event) => setForm((prev) => ({ ...prev, predecessors: [...event.target.selectedOptions].map((option) => option.value) }))}
                >
                  {rows.filter((row) => row.id !== form.id && row.isLeaf).map((row) => (
                    <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <FormError message={error} />
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm">{t('Guardar')}</Button>
              <Button size="sm" onClick={() => setForm(null)}>{t('Cancelar')}</Button>
            </div>
          </form>
        )}
        {!form && <FormError message={error} />}
        {view === 'table' ? (
          <DataTable data={tableRows} columns={columns} searchPlaceholder="Buscar partida..." emptyTitle="Sin partidas" emptyDescription="Crea la primera fase con «Nueva partida»." />
        ) : (
          <Gantt rows={rows} />
        )}
      </Panel>

      <Panel title="Hitos" description="Hitos de obra con fecha objetivo y estado (cumplido / vencido / próximo).">
        {canEdit && (
          <form
            className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3"
            onSubmit={(event) => {
              event.preventDefault();
              const ok = runAction(() => upsertMilestone({ ...milestoneForm, parkId: summary.parkId, wbsItemId: milestoneForm.wbsItemId || null }), setMilestoneError);
              if (ok !== null) setMilestoneForm({ name: '', targetDate: '', wbsItemId: '' });
            }}
          >
            <input className={inputClass} placeholder={t('Nombre del hito')} value={milestoneForm.name} onChange={(event) => setMilestoneForm((prev) => ({ ...prev, name: event.target.value }))} />
            <input type="date" className={inputClass} value={milestoneForm.targetDate} onChange={(event) => setMilestoneForm((prev) => ({ ...prev, targetDate: event.target.value }))} />
            <select className={inputClass} value={milestoneForm.wbsItemId} onChange={(event) => setMilestoneForm((prev) => ({ ...prev, wbsItemId: event.target.value }))}>
              <option value="">{t('Sin partida asociada')}</option>
              {rows.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>)}
            </select>
            <Button type="submit" variant="primary" size="sm" icon={Plus}>{t('Agregar hito')}</Button>
          </form>
        )}
        <FormError message={milestoneError} />
        <DataTable
          data={summary.milestones.map((milestone) => ({ ...milestone, statusLabel: MILESTONE_LABEL[milestone.computedStatus], wbsCode: codeById[milestone.wbsItemId] || '—' }))}
          columns={[
            { key: 'name', label: 'Hito', sortable: true },
            { key: 'targetDate', label: 'Fecha objetivo', sortable: true },
            { key: 'completedDate', label: 'Cumplido el', render: (value) => value || '—' },
            { key: 'wbsCode', label: 'Partida' },
            { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={MILESTONE_TONE[row.computedStatus]}>{t(value)}</Badge> },
            ...(canEdit
              ? [{
                  key: 'id',
                  label: 'Acciones',
                  render: (value, row) => (
                    <Button size="sm" icon={row.completedDate ? Undo2 : CheckCircle2} onClick={() => runAction(() => completeMilestone(value, !row.completedDate), setMilestoneError)}>
                      {t(row.completedDate ? 'Reabrir' : 'Marcar cumplido')}
                    </Button>
                  )
                }]
              : [])
          ]}
          searchPlaceholder="Buscar hito..."
          emptyTitle="Sin hitos"
          emptyDescription="Agrega los hitos clave de la obra."
        />
      </Panel>
    </div>
  );
};

const ConstructionSchedule = () => (
  <ConstructionShell title="Cronograma de obra" description="EDT/WBS, línea base, fechas reales, dependencias e hitos del proyecto en construcción.">
    {(summary) => <ScheduleContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionSchedule;
